// =====================
// Budget Funding Links (Phase 2 of the budgeting rework)
// =====================
//
// Contract-to-budget spine: a many-to-many join between contracts and project
// budgets with an allocated amount per pair. Retainer contracts never appear
// here (they distribute via usage scope). Amounts are numbers, matching the
// contract change-order shapes these views sit beside.

import { z } from 'zod'

// ---- Request bodies ----

export const CreateBudgetFundingLinkSchema = z.object({
  contractId: z.string().uuid(),
  // Null/omitted = "funded by" with no amount specified.
  allocatedAmount: z.number().finite().nonnegative().nullable().optional(),
  notes: z.string().max(2000).nullable().optional(),
})

export type CreateBudgetFundingLinkInput = z.infer<
  typeof CreateBudgetFundingLinkSchema
>

export const UpdateBudgetFundingLinkSchema = z.object({
  allocatedAmount: z.number().finite().nonnegative().nullable().optional(),
  notes: z.string().max(2000).nullable().optional(),
})

export type UpdateBudgetFundingLinkInput = z.infer<
  typeof UpdateBudgetFundingLinkSchema
>

// ---- Responses ----

/** A funding link with enough contract context to render the budget-tab chip. */
export interface BudgetFundingLinkWithContract {
  id: string
  projectBudgetId: string
  contractId: string
  allocatedAmount: number | null
  /** Stored as the contract's currency at link time. */
  currency: string
  notes: string | null
  createdAt: string
  contract: {
    id: string
    title: string
    status: string
    totalValue: number | null
    currency: string
    entityId: string
    entityName: string | null
    /** Set when the contract was soft-deleted; the UI badges the link. */
    deletedAt?: string | null
  }
}

/** Non-retainer contract eligible to fund a project's budget. */
export interface EligibleFundingContract {
  id: string
  title: string
  status: string
  totalValue: number | null
  currency: string
  /** totalValue minus the contract's existing link allocations; null when totalValue is null. */
  unallocatedRemainder: number | null
}

/** One allocation row on the contract coverage view. */
export interface ContractFundingCoverageLink {
  linkId: string
  projectBudgetId: string
  projectId: string
  projectName: string
  allocatedAmount: number | null
  budgetCurrency: string
}

/**
 * Envelope math for the contract detail. Two coexisting decompositions of
 * totalValue: allocated-to-budgets (funding links) and scheduled-to-billing
 * (billing schedule lines). Displayed side by side, never merged.
 */
export interface ContractFundingCoverage {
  contractId: string
  totalValue: number | null
  currency: string
  isRetainer: boolean
  links: ContractFundingCoverageLink[]
  allocatedTotal: number
  /** null when totalValue is null (no envelope math). */
  unallocated: number | null
  isOverallocated: boolean
  /** Sum of schedule lines excluding SKIPPED/CANCELLED and soft-deleted. */
  scheduledTotal: number
  /** null when totalValue is null. */
  unscheduled: number | null
}

/**
 * Change-order handoff (minimal, Phase 2): present when the contract has
 * exactly one funding link, so the UI can offer a budget reforecast.
 */
export interface ChangeOrderBudgetOffer {
  projectId: string
  projectName: string
  delta: number
}
